'use client'

// lib/directus-image-loader.ts

interface ImageLoaderProps {
  src: string
  width: number
  quality?: number
}

// This is your Cloudflare Images account hash.
// Found from your example URL: https://imagedelivery.net/8gld3kOVYe-oeBgrlJJsIw/...
const CLOUDFLARE_ACCOUNT_HASH = '8gld3kOVYe-oeBgrlJJsIw'
const CUSTOM_DOMAIN = 'images.heybossai.com/cdn-cgi/imagedelivery'

// Directus CMS base URL for native asset access
const DIRECTUS_URL = 'https://cms.heybossai.com'

export default function directusImageLoader({
  src,
  width,
  quality,
}: ImageLoaderProps): string {
  // Regular expression to extract a UUID from the src.
  // This will match:
  // - https://cms.heybossai.com/assets/7ca72601-0000-453d-abef-b209eb0674b4
  // - 7ca72601-0000-453d-abef-b209eb0674b4
  // - 7ca72601-0000-453d-abef-b209eb0674b4.jpg
  const uuidRegex =
    /([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/
  const match = src.match(uuidRegex)

  // If no UUID is found, it's not a Directus image.
  // Return the original src. This could be a local image in /public or an external one.
  if (!match) {
    // For local images, Next.js needs a relative path starting with "/"
    if (src.startsWith('http')) {
      return src // Return external URLs as is.
    }
    return src.startsWith('/') ? src : `/${src}`
  }

  const imageId = match[1]

  // For Cloudflare Images, construct the optimized URL
  const params = [`w=${width}`]
  if (quality) {
    params.push(`q=${quality}`)
  }
  params.push('format=auto')
  const paramsString = params.join(',')

  const cloudflareUrl = `https://${CUSTOM_DOMAIN}/${CLOUDFLARE_ACCOUNT_HASH}/assets-${imageId}/${paramsString}`

  // Use Directus native URL with width parameter as fallback
  // This handles newly uploaded images that haven't synced to Cloudflare Images yet
  // Directus supports width transformation via query params
  const directusUrl = `${DIRECTUS_URL}/assets/${imageId}?width=${width}&format=auto`

  // Check if this is a recently uploaded image (created in last 24h)
  // by using Directus native URL which always works
  // Cloudflare Images sync may take time for new uploads
  // Using Directus URL ensures immediate availability
  return directusUrl
}
